<template>
  <b-container fluid class="grid-tools header not-printable">
    <b-row>
      <slot name="quick-filter" v-bind="{ applyFilter, disableControls }"  />
      <b-col cols="auto" v-if="searchEnabled" class="search-col">
        <div class="search-mobile-wrapper" v-click-outside="closeMobileSearch">
          <b-btn
            class="search-toggle-button"
            variant="outline-secondary"
            size="sm"
            :disabled="disableControls"
            @click="toggleMobileSearch"
          >
            <font-awesome-icon icon="search" class="search-toggle-icon" />
          </b-btn>

          <span class="search-box">
            <font-awesome-icon icon="search" class="search-icon" />
            <b-form-input type="search"
                          ref="search"
                          size="sm"
                          :placeholder="`Search ${pluralResourceName}`"
                          :modelValue="searchText"
                          :disabled="disableControls"
                          :lazy="true"
                          @change="applySearch" />
          </span>

          <div v-if="showMobileSearch" class="search-overlay">
            <span class="search-box search-box-overlay">
              <font-awesome-icon icon="search" class="search-icon" />
              <b-form-input type="search"
                            ref="mobileSearch"
                            size="sm"
                            :placeholder="`Search ${pluralResourceName}`"
                            :modelValue="searchText"
                            :disabled="disableControls"
                            :lazy="true"
                            @change="applySearchAndClose" />
            </span>
          </div>
        </div>
      </b-col>
      <b-col cols="auto" v-if="refreshEnabled" class="refresh-col">
        <b-btn class="refresh-toolbar-button" :disabled="disableControls" @click="$emit('refresh')" variant="outline-secondary" size="sm">
          <font-awesome-icon icon="arrow-rotate-right" class="refresh-toolbar-icon" />
        </b-btn>
      </b-col>
      <b-col cols="auto" v-if="filterEnabled" class="filter-col">
        <b-btn @click="$emit('update:filterOpen', !filterOpen)"
          :variant="filterInUse ? 'warning' : 'outline-secondary'"
          class="filter-toggle-button"
          :disabled="disableControls"
          size="sm"
        >
          <font-awesome-icon icon="filter" class="filter-toggle-icon" />
          <span class="filter-toggle-label">Filter</span>
        </b-btn>

        <filter-drawer
          :visible="filterOpen"
          :disabled="disableControls"
          @update:visible="$emit('update:filterOpen', $event)"
          @pinned="$emit('customize-pinned', $event)"
          @apply="$emit('applyFilter')"
          @clear="$emit('clearFilter')"
        >
          <slot name="filter-form" />
        </filter-drawer>

      </b-col>
      <b-col cols="auto" class="column-menu-col">
        <column-menu-button
          buttonClass="column-menu-toolbar-button"
          :allColumns="allColumns"
          :columnLabelKey="columnLabelKey"
          :columnIsSelected="columnIsSelected"
          :disabled="disableControls"
          :primevue="true"
          size="sm"
          @selectedColumnsUpdated="$emit('selectedColumnsUpdated', $event)"
          @reset-to-defaults="$emit('reset-columns-to-defaults')"
        />
      </b-col>
      <b-col cols="auto" class="export-col" :class="allowBulkSelect ? null : 'mr-auto'">
        <b-dropdown text="Export" variant="outline-secondary" class="export-toolbar-dropdown" :disabled="disableControls || !exportEnabled" size="sm">
          <template #button-content>
            <font-awesome-icon icon="file-export" class="export-toolbar-icon" />
            <span class="export-toolbar-label">Export</span>
          </template>
          <b-dropdown-item @click="$emit('csv')"><font-awesome-icon icon="file-csv" /> CSV</b-dropdown-item>
          <b-dropdown-item @click="$emit('excel')"><font-awesome-icon icon="file-excel" /> Excel</b-dropdown-item>
          <b-dropdown-item  @click="$emit('pdf')"><font-awesome-icon icon="file-pdf" /> PDF</b-dropdown-item>
          <b-dropdown-item @click="$emit('print')" v-if="printEnabled"><font-awesome-icon icon="print" /> Print</b-dropdown-item>

          <template v-if="isAppAdmin">
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item @click="reloadTemplates">
              <font-awesome-icon icon="rotate-right" />
              Reload templates
            </b-dropdown-item>
          </template>

          <template v-if="customExportOptions && customExportOptions.length > 0">
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item
              v-for="customExportOption in customExportOptions"
              :key="customExportOption.id"
              @click="exportToCustom(customExportOption.id)"
            >
              <!-- TODO: Infer specific icon from template attributes. -->
              <font-awesome-icon icon="file-export" />
              {{ customExportOption.label }}
            </b-dropdown-item>
          </template>
        </b-dropdown>
      </b-col>
      <b-col cols="auto" class="bulk-select-col mr-auto" v-if="allowBulkSelect">
        <b-btn v-if="!bulkSelectOn" variant="outline-secondary" :disabled="disableControls" size="sm"
          class="bulk-select-button"
          @click="$emit('bulk-select-clicked')"
        >
          <font-awesome-icon icon="check-double" class="bulk-select-icon tw:mr-1" />
          <span class="bulk-select-label">Bulk Select</span>
        </b-btn>
        <b-dropdown v-else
          size="sm"
          :split="true"
          variant="warning"
          class="bulk-action-dropdown"
        >
          <template #button-content>
            <div @click="$emit('bulk-select-clicked')" class="bulk-action-button-content">
              <font-awesome-icon icon="check-double" class="bulk-action-icon tw:mr-1" />
              <span class="bulk-action-label">Bulk Action</span>
            </div>
          </template>
          <b-dropdown-item v-for="bulkSelectOption in bulkSelectOptions" :key="bulkSelectOption.id"
            :disabled="bulkSelectOptionsDisabled"
            @click="$emit('bulk-action-selected', bulkSelectOption.id)"
          >
            {{ bulkSelectOption.label }}
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
      <b-col cols="auto" class="add-col" v-if="addItemEnabled">
        <b-dropdown :split="importEnabled || !!addOptions" :no-caret="!importEnabled" variant="primary" class="add-toolbar-dropdown" :disabled="disableControls" size="sm">
          <template #button-content>
            <div @click="$emit('addItem')" class="add-button-content">
              <font-awesome-layers class="fa-lg add-button-icon" v-if="icon">
                <font-awesome-icon :icon="icon" transform="down-1" />
                <font-awesome-icon icon="plus" transform="shrink-6 right-14" />
              </font-awesome-layers>
              <font-awesome-icon icon="plus" class="add-button-icon" v-else />
              <span class="add-button-label">{{ addResourceTextComputed }}</span>
            </div>
          </template>
          <b-dropdown-item @click="importFromSpreadsheet" v-if="importEnabled">
            <font-awesome-icon icon="file-import" /> Import from spreadsheet
          </b-dropdown-item>
          <template v-if="addOptions">
            <b-dropdown-item v-for="(addOption, index) in addOptions" :key="index" @click="addOption.onClick">
              <font-awesome-icon :icon="addOption.icon" /> {{ addOption.text }}
            </b-dropdown-item>
          </template>
        </b-dropdown>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
import { pluralize } from 'inflection'
import ColumnMenuButton from '@/components/ColumnMenuButton.vue'
import FilterDrawer from '@/components/FilterDrawer.vue'
import _ from 'lodash'
import { startBulkImport } from '@/bulkImport'
import { mapState } from 'vuex'
import { useBootstrapVueModal } from '@/composables/useBootstrapVueModal'

export default {
  data () {
    return {
      showMobileSearch: false
    }
  },
  setup () {
    const { hideModal, showModal } = useBootstrapVueModal()
    return {
      showModal,
      hideModal
    }
  },
  components: {
    ColumnMenuButton,
    FilterDrawer
  },
  props: {
    searchEnabled: {
      type: Boolean,
      default: true
    },
    searchText: String,
    filterEnabled: {
      type: Boolean,
      default: true
    },
    refreshEnabled: {
      type: Boolean,
      default: false
    },
    filterOpen: {
      type: Boolean,
      default: false
    },
    filterInUse: {
      type: Boolean,
      default: false
    },
    exportEnabled: {
      type: Boolean,
      default: true
    },
    printEnabled: {
      type: Boolean,
      default: true
    },
    resourceName: {
      type: String,
      default: 'Item'
    },
    addItemEnabled: {
      type: Boolean,
      default: true
    },
    importEnabled: {
      type: Boolean,
      default: true
    },
    // If importEnabled but no importEnabled is defined, then a generic modal
    // will be displayed instructing the user to email support with a spreadsheet.
    bulkImportConfig: Function,
    addResourceText: {
      type: String
    },
    icon: String,
    addOptions: Array,
    allColumns: {
      type: Array,
      default: () => []
    },
    columnLabelKey: String,
    columnIsSelected: Function,
    // TODO: Bind disableControls to all buttons.
    disableControls: {
      type: Boolean,
      default: false
    },
    allowBulkSelect: Boolean,
    bulkSelectOptions: Array,
    bulkSelectOptionsDisabled: Boolean,
    bulkSelectOn: Boolean,
    customExportOptions: Array
  },
  emits: [
    'addItem',
    'applyFilter',
    'applySearch',
    'bulk-action-selected',
    'bulk-import-completed',
    'bulk-select-clicked',
    'clearFilter',
    'csv',
    'customize-pinned',
    'excel',
    'export-to-custom',
    'pdf',
    'print',
    'refresh',
    'reset-columns-to-defaults',
    'selectedColumnsUpdated',
    'update:filterOpen'
  ],
  computed: {
    ...mapState('userProfile', ['isAppAdmin']),
    pluralResourceName () {
      return pluralize(this.resourceName)
    },
    addResourceTextComputed () {
      return this.addResourceText || `Add ${this.resourceName}`
    }
  },
  methods: {
    applyFilter () {
      this.$emit('applyFilter')
    },
    applySearch (event) {
      this.$emit('applySearch', event.target.value)
    },
    applySearchAndClose (event) {
      this.applySearch(event)
      this.closeMobileSearch()
    },
    toggleMobileSearch () {
      this.showMobileSearch = !this.showMobileSearch
      if (this.showMobileSearch) {
        this.$nextTick(() => {
          if (this.$refs.mobileSearch?.focus) this.$refs.mobileSearch.focus()
        })
      }
    },
    closeMobileSearch () {
      this.showMobileSearch = false
    },
    async importFromSpreadsheet () {
      if (this.bulkImportConfig) {
        const bulkImportConfig = await this.bulkImportConfig()
        startBulkImport(Object.assign({
          showModal: this.showModal,
          resourceName: this.pluralResourceName
        }, bulkImportConfig))
          .then(dataWasImported => {
            if (dataWasImported) this.$emit('bulk-import-completed')
          })
      } else {
        this.showModal({
          component: (await import('./ImportListModal.vue')).default
        })
      }
    },
    exportToCustom (customOptionId) {
      this.$emit('export-to-custom', customOptionId)
    },
    reloadTemplates () {
      this.$store.dispatch('integrationTemplates/load', true)
    }
  }
}
</script>
<style scoped>

.header {
  padding: 0;
}

.header :deep(.row) {
  margin-top: -10px;
  & > div {
    margin-top: 10px;
  }
}

.search-col {
  padding-right: 0;
}

.filter-col,
.column-menu-col,
.export-col,
.bulk-select-col {
  padding-right: 0;
}

.search-mobile-wrapper {
  position: relative;
}

.search-toggle-button {
  display: none;
}

.search-overlay {
  position: absolute;
  top: calc(100% + .5rem);
  left: 0;
  width: min(320px, calc(100vw - 1rem));
  z-index: 1200;
}

.search-box-overlay {
  width: 100%;
  border-radius: .5rem;
}

.refresh-col {
  padding-right: 0;
  .btn {
    padding-left: 7px;
    padding-right: 0;
  }
}

.search-box {
  display: flex;
  background-color: var(--color-slate-955);
  border: 1px solid var(--color-slate-800);
  color: var(--color-slate-100);
  /* padding: 0px 10px; */

  .search-icon, .fa-layers {
    margin: auto 10px;
  }
}
.search-box  :deep(input) {
  border: none;
  background-color: transparent !important;
  color: inherit !important;

  &::placeholder {
    color: var(--color-slate-400);
  }

  &:focus {
    border: none;
    outline: none;
    box-shadow: none;
  }
}

:global(.light-theme) .search-box {
  background-color: white;
  border: 1px solid #999;
  color: #999;

  :deep(input) {
    color: #333 !important;
    &::placeholder {
      color: #bbb;
    }
  }
}

.grid-tools {
  .add-button-content {
    margin-left: 5px;
    display: inline-flex;
    align-items: center;
  }

  .add-button-icon {
    margin-left: .25rem;
    margin-right: 1rem;
  }

  .bulk-action-dropdown :deep(.dropdown-menu) {
      /* Bulk action list can get quite long, so make it scrollable. */
      max-height: calc(100vh - 250px);
      overflow-y: auto;
  }
}

@media (min-width: 992px) {
  .add-col {
    margin-left: auto;
  }
}

@media (max-width: 991.98px) {
  .grid-tools {
    --toolbar-compact-btn-size: 32px;
    --toolbar-compact-icon-size: 14px;

    :deep(.row) {
      --bs-gutter-x: .45rem;
      --bs-gutter-y: .625rem;
    }

    .search-box {
      display: none;
    }

    .search-toggle-button {
      display: inline-flex;
      width: var(--toolbar-compact-btn-size);
      height: var(--toolbar-compact-btn-size);
      padding: 0;
      align-items: center;
      justify-content: center;
      border-radius: .25rem;
      line-height: 1;
    }

    .search-toggle-button :deep(.svg-inline--fa) {
      margin: 0;
      line-height: 1;
    }

    .search-toggle-icon {
      font-size: var(--toolbar-compact-icon-size);
    }

    .refresh-col {
      .btn {
        width: var(--toolbar-compact-btn-size);
        height: var(--toolbar-compact-btn-size);
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: .25rem;
        line-height: 1;
      }
    }

    .refresh-col :deep(.svg-inline--fa) {
      margin: 0;
    }

    .refresh-toolbar-icon {
      font-size: var(--toolbar-compact-icon-size);
    }

    .search-box-overlay {
      display: flex;
    }

    :deep(.column-menu-toolbar-button .column-button-label) {
      display: none;
    }

    :deep(.column-menu-button) {
      margin: 0;
      padding: 0;
      display: inline-flex;
      align-items: center;
    }

    :deep(.column-menu-toolbar-button.p-button) {
      width: var(--toolbar-compact-btn-size);
      height: var(--toolbar-compact-btn-size);
      min-width: var(--toolbar-compact-btn-size);
      max-width: var(--toolbar-compact-btn-size);
      min-height: var(--toolbar-compact-btn-size);
      max-height: var(--toolbar-compact-btn-size);
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: .25rem;
      line-height: 1;
      margin: 0;
      box-sizing: border-box;
      color: #6c757d;
      border-color: #6c757d;
      background: transparent;
    }

    :deep(.column-menu-toolbar-button.p-button:not(:disabled):hover) {
      color: #6c757d;
      border-color: #6c757d;
      background: transparent;
    }

    :deep(.column-menu-toolbar-button .column-button-icon) {
      margin-right: 0;
      font-size: var(--toolbar-compact-icon-size);
      color: currentColor;
    }

    .filter-toggle-button {
      width: var(--toolbar-compact-btn-size);
      height: var(--toolbar-compact-btn-size);
      padding: 0;
      border-radius: .25rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .filter-toggle-icon {
      margin-right: 0;
      font-size: var(--toolbar-compact-icon-size);
    }

    .filter-toggle-label,
    .export-toolbar-label,
    .add-button-label,
    .bulk-select-label,
    .bulk-action-label {
      display: none;
    }

    .bulk-select-button {
      width: var(--toolbar-compact-btn-size);
      height: var(--toolbar-compact-btn-size);
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: .25rem;
      line-height: 1;
    }

    .bulk-select-icon,
    .bulk-action-icon {
      margin-right: 0;
      font-size: var(--toolbar-compact-icon-size);
    }

    .bulk-action-dropdown .bulk-action-button-content {
      margin: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .bulk-action-dropdown :deep(.btn:first-child) {
      width: var(--toolbar-compact-btn-size);
      min-width: var(--toolbar-compact-btn-size);
      height: var(--toolbar-compact-btn-size);
      min-height: var(--toolbar-compact-btn-size);
      max-height: var(--toolbar-compact-btn-size);
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .bulk-action-dropdown :deep(.dropdown-toggle-split) {
      min-width: var(--toolbar-compact-btn-size);
      height: var(--toolbar-compact-btn-size);
      min-height: var(--toolbar-compact-btn-size);
      max-height: var(--toolbar-compact-btn-size);
      padding-left: .35rem;
      padding-right: .35rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .export-toolbar-dropdown :deep(.btn) {
      width: var(--toolbar-compact-btn-size);
      height: var(--toolbar-compact-btn-size);
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: .25rem;
      line-height: 1;
    }

    .export-toolbar-dropdown :deep(.dropdown-toggle::after) {
      display: none;
    }

    .export-toolbar-icon {
      margin-right: 0;
      font-size: var(--toolbar-compact-icon-size);
    }

    .add-toolbar-dropdown .add-button-content {
      margin-left: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .add-toolbar-dropdown :deep(.btn:first-child) {
      min-width: 34px;
      height: var(--toolbar-compact-btn-size);
      min-height: var(--toolbar-compact-btn-size);
      max-height: var(--toolbar-compact-btn-size);
      padding-left: .45rem;
      padding-right: .45rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .add-toolbar-dropdown :deep(.dropdown-toggle-split) {
      min-width: var(--toolbar-compact-btn-size);
      height: var(--toolbar-compact-btn-size);
      min-height: var(--toolbar-compact-btn-size);
      max-height: var(--toolbar-compact-btn-size);
      padding-left: .35rem;
      padding-right: .35rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .add-button-icon {
      margin: 0;
      font-size: var(--toolbar-compact-icon-size);
    }

    .add-toolbar-dropdown :deep(.add-button-content .fa-layers) {
      font-size: var(--toolbar-compact-icon-size);
    }
  }
}
</style>
